import { useSelector } from '@/libs/state'
import { session$ } from '@/features/auth/store'
import { canActor, resolveItemPhaseTeamId } from '../domain/authorization'
import { canTransitionStatus } from '../domain/work-item-status'
import { hasSenderPhase } from '../domain/phase-ledger'
import type { Phase, WorkItem } from '../domain/workflow'

type WorkItemActorCan = {
	assign: boolean
	complete: boolean
	cancel: boolean
	reopen: boolean
	archive: boolean
	accept: boolean
	reject: boolean
	comment: boolean
	start: boolean
	pause: boolean
}

// Derives per-action authorization for the current session user against a work
// item. Intersects status-transition guards with canActor role/team rules.
// Returns all-false when item is null (sheet is closed / no item selected).
const useWorkItemActorCan = (item: WorkItem | null, phases: Phase[]): WorkItemActorCan => {
	const user = useSelector(() => session$.user.get())

	const ctx = {
		actorId: user?.id ?? 'system',
		actorName: user?.fullName ?? 'Sistema',
		companyRole: user?.companyRole,
		teamIds: user?.teamIds,
	}

	if (!item) {
		return {
			assign: false,
			complete: false,
			cancel: false,
			reopen: false,
			archive: false,
			accept: false,
			reject: false,
			comment: false,
			start: false,
			pause: false,
		}
	}

	const phaseTeamId = resolveItemPhaseTeamId(phases, item)
	const status = item.status
	const currentPhase = phases.find(p => p.id === item.currentPhaseId)
	const currentStep = currentPhase?.teamWorkflow.steps.find(s => s.id === item.currentStepId)

	return {
		assign:
			(status === 'inbox' || status === 'open') &&
			canActor('assign', item, ctx, phaseTeamId),
		complete:
			canTransitionStatus(status, 'completed') &&
			currentStep?.canComplete === true &&
			canActor('complete', item, ctx, phaseTeamId),
		cancel:
			canTransitionStatus(status, 'cancelled') &&
			canActor('cancel', item, ctx, phaseTeamId),
		reopen:
			canTransitionStatus(status, 'open') &&
			(status === 'completed' || status === 'cancelled') &&
			canActor('reopen', item, ctx, phaseTeamId),
		archive:
			canTransitionStatus(status, 'archived') &&
			canActor('archive', item, ctx, phaseTeamId),
		accept: status === 'awaiting_handoff' && canActor('accept_handoff', item, ctx, phaseTeamId),
		reject: status === 'awaiting_handoff' && hasSenderPhase(item) && canActor('reject_handoff', item, ctx, phaseTeamId),
		comment: status !== 'archived' && canActor('comment', item, ctx, phaseTeamId),
		start:
			status === 'open' &&
			item.workState === 'todo' &&
			canActor('set_work_state', item, ctx, phaseTeamId),
		pause:
			status === 'open' &&
			item.workState === 'in_progress' &&
			canActor('set_work_state', item, ctx, phaseTeamId),
	}
}

export type { WorkItemActorCan }
export { useWorkItemActorCan }
